"use client";

import React, { useEffect, useRef, useState } from "react";
import { Pencil, WandSparkles, Paintbrush, Send } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { cn } from "@/lib/utils";
type UseRevealOnScrollOptions = {
  once?: boolean;
  threshold?: number | number[];
  rootMargin?: string;
};
function useRevealOnScroll<T extends HTMLElement = HTMLElement>({
  once = true,
  threshold = 0.2,
  rootMargin = "0px"
}: UseRevealOnScrollOptions = {}) {
  const ref = useRef<T | null>(null);
  const [isVisible, setIsVisible] = useState(false);
  useEffect(() => {
    const node = ref.current;
    if (!node) {
      return;
    }
    if (typeof window === "undefined" || typeof IntersectionObserver === "undefined") {
      setIsVisible(true);
      return;
    }
    const observer = new IntersectionObserver(entries => {
      const entry = entries[0];
      if (entry?.isIntersecting) {
        setIsVisible(true);
        if (once) {
          observer.unobserve(entry.target);
        }
      } else if (!once) {
        setIsVisible(false);
      }
    }, {
      threshold,
      rootMargin
    });
    observer.observe(node);
    return () => {
      observer.disconnect();
    };
  }, [once, rootMargin, threshold]);
  return {
    ref,
    isVisible
  };
}
const roadmapSteps = [{
  id: "01",
  title: "Formulation",
  description: "Developing custom probiotic fizzes and adaptogen seltzers tailored to your brand's unique wellness profile and functional goals.",
  icon: Pencil
}, {
  id: "02",
  title: "R&D Testing",
  description: "Rigorous laboratory analysis and counter-pressure sampling to ensure perfect carbonation, shelf stability, and flavor consistency.",
  icon: WandSparkles
}, {
  id: "03",
  title: "Compliance",
  description: "Navigating FDA registration and nutritional labeling requirements to meet strict industry standards for functional nutraceuticals.",
  icon: Paintbrush
}, {
  id: "04",
  title: "Production",
  description: "High-speed canning and precision logistics to deliver your finished RTD products to market with technical excellence.",
  icon: Send
}];
function TimelineItem({
  step,
  index
}: {
  step: (typeof roadmapSteps)[0];
  index: number;
}) {
  const {
    ref,
    isVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.3
  });
  const isEven = index % 2 === 0;
  const Icon = step.icon;
  return <div data-section-id="93842" data-index={index} ref={ref} className={cn("relative flex items-center justify-between md:justify-normal w-full mb-16 last:mb-0 group", isEven ? "md:flex-row-reverse" : "md:flex-row", isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-12", "transition-all duration-700 ease-out")}>
      {}
      <div className="absolute left-[2.25rem] md:left-1/2 w-12 h-12 rounded-full bg-background border-2 border-primary flex items-center justify-center -translate-x-1/2 z-10 shadow-[0_0_15px_rgba(var(--color-primary),0.2)] group-hover:scale-110 group-hover:shadow-[0_0_25px_rgba(var(--color-primary),0.4)] transition-all duration-300">
        <Icon className="w-5 h-5 text-primary group-hover:animate-pulse" />
      </div>

      {}
      <div className={cn("w-[calc(100%-6rem)] ml-auto md:w-[calc(50%-4rem)] md:mx-0", isEven ? "md:text-right" : "md:text-left")}>
        <Card className="relative overflow-hidden bg-card/40 backdrop-blur-md border-border/50 hover:border-primary/30 transition-colors duration-500 shadow-lg rounded-lg">
          {}
          <span className={cn("absolute -top-8 text-9xl font-mono font-black text-transparent [-webkit-text-stroke:2px_var(--color-primary)] opacity-10 pointer-events-none select-none transition-transform duration-700 group-hover:scale-105 group-hover:opacity-20", isEven ? "md:-left-8 right-4 md:right-auto" : "-right-8")}>
            {step.id}
          </span>

          <CardHeader className="relative z-10 pb-2">
            <CardTitle className="text-2xl font-sans font-semibold tracking-tight text-foreground group-hover:text-primary transition-colors duration-300">
              {step.title}
            </CardTitle>
          </CardHeader>
          <CardContent className="relative z-10">
            <p className="text-muted-foreground leading-relaxed font-sans">
              {step.description}
            </p>
          </CardContent>
        </Card>
      </div>
    </div>;
}
export default function CreationRoadmap() {
  const {
    ref: headerRef,
    isVisible: isHeaderVisible
  } = useRevealOnScroll<HTMLDivElement>();
  return <section id="our-development-process" className="dark bg-background text-foreground relative overflow-hidden py-24 md:py-32">
      {}
      <div className="absolute inset-0 pointer-events-none opacity-[0.05]" style={{
      backgroundImage: `
            linear-gradient(to right, var(--color-primary) 1px, transparent 1px),
            linear-gradient(to bottom, var(--color-primary) 1px, transparent 1px)
          `,
      backgroundSize: "40px 40px"
    }} aria-hidden="true" />

      <div className="container mx-auto px-4 relative z-10">
        {}
        <div ref={headerRef} className={cn("max-w-2xl mx-auto text-center mb-20", isHeaderVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8", "transition-all duration-700 ease-out")}>
          <h2 className="text-sm font-mono tracking-widest text-primary uppercase mb-4">
            The Process
          </h2>
          <h3 className="text-4xl md:text-5xl font-sans font-bold mb-6 tracking-tighter">
            Development Roadmap
          </h3>
          <p className="text-lg text-muted-foreground font-sans">
            A transparent look into how we transform your functional beverage ideas into market-ready, high-quality aluminum cans, step by technical step.
          </p>
        </div>

        {}
        <div className="relative max-w-5xl mx-auto">
          {}
          <div className="absolute left-[2.25rem] md:left-1/2 top-4 bottom-4 w-px bg-gradient-to-b from-transparent via-border to-transparent md:-translate-x-1/2" />

          {}
          <div className="relative flex flex-col">
            {roadmapSteps.map((step, index) => <TimelineItem key={step.id} step={step} index={index} />)}
          </div>
        </div>
      </div>
    </section>;
}